import React, { useState } from "react";
import { Search, Ticket, Star, CalendarDays, MapPin } from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   FILE: src/SUPERAPP/BOOKING/TICKETS/TicketsHome.tsx
   Facebook-style theme: blue / white / black. Self-contained —
   swap MOCK_EVENTS for a real fetch/query when ready.
═══════════════════════════════════════════════════════════ */

const FB = {
  bg: "#F0F2F5",
  panelBg: "#FFFFFF",
  black: "#050505",
  textMuted: "#65676B",
  blue: "#1877F2",
  blueSoft: "#E7F3FF",
  border: "#DADDE1",
};

interface EventItem {
  id: number;
  name: string;
  venue: string;
  date: string;
  price: string;
  rating: number;
}

const MOCK_EVENTS: EventItem[] = [
  { id: 1, name: "Indie Rock Night", venue: "The Fillmore", date: "Jul 12", price: "$35.00", rating: 4.7 },
  { id: 2, name: "Comedy Showcase", venue: "Laugh Factory", date: "Jul 15", price: "$28.00", rating: 4.5 },
  { id: 3, name: "City Basketball Finals", venue: "Civic Arena", date: "Jul 19", price: "$65.00", rating: 4.8 },
  { id: 4, name: "Jazz in the Park", venue: "Riverside Amphitheater", date: "Jul 22", price: "$20.00", rating: 4.6 },
  { id: 5, name: "Broadway: The Musical", venue: "Grand Theater", date: "Jul 26", price: "$95.00", rating: 4.9 },
  { id: 6, name: "Food & Wine Festival", venue: "Expo Hall", date: "Aug 2", price: "$40.00", rating: 4.4 },
];

const TicketsHome: React.FC = () => {
  const [query, setQuery] = useState("");

  const filtered = MOCK_EVENTS.filter(
    (e) =>
      e.name.toLowerCase().includes(query.toLowerCase()) ||
      e.venue.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div style={S.wrap}>
      <div style={S.searchBar}>
        <Search size={16} color={FB.textMuted} />
        <input
          style={S.searchInput}
          placeholder="Search events or venues"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div style={S.grid}>
        {filtered.map((e) => (
          <div key={e.id} style={S.card}>
            <div style={S.thumb}>
              <Ticket size={24} color={FB.blue} />
            </div>
            <div style={S.cardBody}>
              <div style={S.cardVenue}>
                <MapPin size={11} /> {e.venue}
              </div>
              <div style={S.cardName}>{e.name}</div>
              <div style={S.cardDate}>
                <CalendarDays size={12} /> {e.date}
              </div>
              <div style={S.cardBottomRow}>
                <span style={S.cardPrice}>{e.price}</span>
                <span style={S.cardRating}>
                  <Star size={12} color="#F5A623" fill="#F5A623" /> {e.rating}
                </span>
              </div>
              <button style={S.addBtn}>Get tickets</button>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={S.empty}>No events match "{query}".</div>
        )}
      </div>
    </div>
  );
};

export default TicketsHome;

const S: Record<string, React.CSSProperties> = {
  wrap: { padding: "16px 20px 40px", maxWidth: 980, margin: "0 auto" },
  searchBar: {
    display: "flex", alignItems: "center", gap: 8, background: FB.panelBg,
    border: `1px solid ${FB.border}`, borderRadius: 20, padding: "8px 14px", marginBottom: 18,
  },
  searchInput: { border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent", color: FB.black },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 14 },
  card: { background: FB.panelBg, border: `1px solid ${FB.border}`, borderRadius: 10, overflow: "hidden" },
  thumb: { height: 90, background: FB.blueSoft, display: "flex", alignItems: "center", justifyContent: "center" },
  cardBody: { padding: "10px 12px 12px" },
  cardVenue: { fontSize: 11, color: FB.textMuted, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 },
  cardName: { fontSize: 14, fontWeight: 700, color: FB.black, margin: "2px 0 6px" },
  cardDate: { fontSize: 12, color: FB.textMuted, display: "flex", alignItems: "center", gap: 4, marginBottom: 8 },
  cardBottomRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  cardPrice: { fontSize: 14, fontWeight: 800, color: FB.black },
  cardRating: { fontSize: 12, color: FB.textMuted, display: "flex", alignItems: "center", gap: 3 },
  addBtn: {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: FB.blue, color: "#fff", border: "none", borderRadius: 6, padding: "8px 0",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  empty: { gridColumn: "1 / -1", textAlign: "center", color: FB.textMuted, fontSize: 13.5, padding: "40px 0" },
};
